import React, { useState, useRef, useEffect } from 'react';
import { Field, ErrorMessage, getIn } from 'formik';
import TextError from 'components/global/form/TextError';
import { FaCalendarAlt } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';

const InputDate = (props) => {
  const { label, name, formik, readOnly = false, dateSeparator = '/', ...rest } = props;

  const [focused, setFocused] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const wrapperRef = useRef(null);

  // Nested form values
  const value = getIn(formik.values, name);
  const isTouched = getIn(formik.touched, name);
  const error = getIn(formik.errors, name);
  const isError = isTouched && error;

  // Parse date-only values in local time so the calendar keeps the selected day.
  const localValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const formattedValue =
    localValue && !isNaN(new Date(localValue)) ? new Date(localValue) : null;

  const isActive = focused || formattedValue;

  // Container classes
  const containerClasses =
    readOnly
      ? 'backdrop-blur-sm bg-gray-100/50 cursor-not-allowed border-gray-200'
      : isError
      ? 'border-red-500'
      : focused
      ? 'border-primary shadow-md backdrop-blur-md bg-white/70'
      : 'border-lighter bg-white/50';

  // Close datepicker on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDatePicker(false);
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ⭐ UI Display (MM/DD/YYYY)
  const displayDate = formattedValue
  ? format(formattedValue, 'dd/MMM/yyyy')
  : '';

  return (
    <div className="relative form-row" ref={wrapperRef}>
      {/* Input container */}
      <div className={`flex items-center border rounded-lg relative ${containerClasses}`}>
        <FaCalendarAlt className="text-gray-400 z-0 absolute right-3" />

        {/* Input */}
        <Field
          id={name}
          name={name}
          type="text"
          readOnly
          value={displayDate}
          onFocus={() => {
            if (!readOnly) {
              setFocused(true);
              setShowDatePicker(true);
            }
          }}
          className={`w-full py-3 pl-10 pr-4 rounded-lg outline-none transition-all duration-200
            ${
              readOnly
                ? 'text-gray-500 bg-transparent select-none'
                : focused
                ? 'text-primary bg-transparent'
                : isError
                ? 'text-red-600 bg-transparent'
                : 'text-gray-500 bg-transparent'
            }
          `}
          {...rest}
        />

        {/* Floating Label */}
        <label
          htmlFor={name}
          className={`absolute left-4 bg-white px-1 transition-all duration-300 pointer-events-none
            ${
              isActive
                ? `top-[-12px] text-[13px] ${
                    isError
                      ? 'text-red-500'
                      : focused
                      ? 'text-primary'
                      : 'text-gray-600'
                  }`
                : 'top-3 text-gray-400'
            }
          `}
        >
          {label}
        </label>
      </div>

      {/* Error */}
      <ErrorMessage component={TextError} name={name} />

      {/* DatePicker */}
      {showDatePicker && (
        <div className="absolute z-50 top-[60px]">
          <DatePicker
            selected={formattedValue}
            inline
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            onChange={(date) => {
              if (date) {
                // Preserve the field's date separator in Formik.
                const formatted = `${date.getFullYear()}${dateSeparator}${String(
                  date.getMonth() + 1
                ).padStart(2, '0')}${dateSeparator}${String(date.getDate()).padStart(2, '0')}`;

                formik.setFieldValue(name, formatted);
              } else {
                formik.setFieldValue(name, '');
              }

              setShowDatePicker(false);
              setFocused(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InputDate;
